import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WarehouseFilters, FindWarehouses } from 'components/Pages/Warehouses';
import {
  ListingPageHeader,
  Paginator,
  SettingsMenu,
  SortableHeader,
  SortableHeadersGroup,
} from 'components/Shared';
import { usePaginationCache } from 'hooks';
import { useWarehouseOrderEventAutomations } from 'hooks/api/useWarehouseOrderEventAutomations';
import { Scaffold } from 'layouts';
import { apiErrorHandler, notify } from 'services';
import type { AppDispatch, RootState } from 'store';
import { PaginateWarehousesActions as PaginateActions } from 'store/ducks/warehouses';
import { Formatter } from 'utils';
import * as S from './styles';

type AutomationWarehouseQuery = FindWarehouses & {
  orderEventAutomationSource?: 'YMS_PORTARIA';
};

export const WarehouseOrderEventAutomationsPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    getWarehouseOrderEventAutomations,
    replaceWarehouseOrderEventAutomations,
  } = useWarehouseOrderEventAutomations();
  const { paginationCache, updatePaginationCache, handleFilter, handleSort } =
    usePaginationCache('warehouse-order-event-automations');
  const {
    data: warehouses,
    pagination,
    loading,
  } = useSelector((state: RootState) => state.paginateWarehouses);
  const [query, setQuery] = useState<AutomationWarehouseQuery>({
    search: '',
    searchField: 'name',
    page: 1,
    limit: 10,
    ...paginationCache,
  });
  const [automaticWarehouseIds, setAutomaticWarehouseIds] = useState<number[]>(
    []
  );
  const [persistedWarehouseIds, setPersistedWarehouseIds] = useState<number[]>(
    []
  );
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(true);
  const [configurationError, setConfigurationError] = useState<string | null>(
    null
  );
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = useMemo(
    () =>
      [...automaticWarehouseIds].sort().join(',') !==
      [...persistedWarehouseIds].sort().join(','),
    [automaticWarehouseIds, persistedWarehouseIds]
  );

  useEffect(() => {
    dispatch(PaginateActions.request(query));
  }, [dispatch, query]);

  useEffect(() => {
    let active = true;
    setIsLoadingConfiguration(true);
    setConfigurationError(null);
    getWarehouseOrderEventAutomations()
      .then((configurations) => {
        if (!active) return;
        const warehouseIds = configurations.map(
          ({ warehouseId }) => warehouseId
        );
        setAutomaticWarehouseIds(warehouseIds);
        setPersistedWarehouseIds(warehouseIds);
      })
      .catch((error) => {
        if (!active) return;
        const message = apiErrorHandler(error).errorMessage;
        setConfigurationError(message);
        notify('error', message);
      })
      .finally(() => {
        if (active) setIsLoadingConfiguration(false);
      });
    return () => {
      active = false;
    };
  }, [getWarehouseOrderEventAutomations, loadAttempt]);

  useEffect(() => {
    return () => {
      dispatch(PaginateActions.reset());
      updatePaginationCache(query);
    };
  }, [dispatch, query, updatePaginationCache]);

  const toggleWarehouse = useCallback((warehouseId: number): void => {
    setAutomaticWarehouseIds((current) =>
      current.includes(warehouseId)
        ? current.filter((id) => id !== warehouseId)
        : [...current, warehouseId]
    );
  }, []);

  const save = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    try {
      const configurations = await replaceWarehouseOrderEventAutomations(
        automaticWarehouseIds
      );
      const warehouseIds = configurations.map(({ warehouseId }) => warehouseId);
      setAutomaticWarehouseIds(warehouseIds);
      setPersistedWarehouseIds(warehouseIds);
      if (query.orderEventAutomationSource) {
        dispatch(PaginateActions.request(query));
      }
      notify('success', 'Automação de apontamentos atualizada com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [
    automaticWarehouseIds,
    dispatch,
    query,
    replaceWarehouseOrderEventAutomations,
  ]);

  return (
    <Scaffold>
      <SettingsMenu />
      <S.MainPanel>
        <ListingPageHeader
          icon={<S.WarehouseIcon />}
          title="Automação de apontamentos"
          isLoading={loading || isLoadingConfiguration || isSaving}
          actions={
            <S.SaveButton
              type="button"
              size="small"
              disabled={
                isLoadingConfiguration ||
                isSaving ||
                Boolean(configurationError) ||
                !isDirty
              }
              onClick={save}
            >
              Salvar
            </S.SaveButton>
          }
        />
        <S.Description>
          Selecione os armazéns cujos apontamentos serão atualizados
          automaticamente pelo sistema Jornada do Veículo (YMS Portaria). O
          padrão para os demais armazéns é manual.
        </S.Description>
        {configurationError && (
          <S.ErrorMessage role="alert">
            {configurationError}
            <S.RetryButton
              type="button"
              size="small"
              onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            >
              Tentar novamente
            </S.RetryButton>
          </S.ErrorMessage>
        )}
        <WarehouseFilters
          currentFilter={query}
          searchFields={['name', 'document']}
          onFilter={(filter) => handleFilter(query, filter, setQuery)}
        />
        <S.SavedOnlyFilter>
          <S.Checkbox
            id="saved-warehouses-only"
            type="checkbox"
            checked={Boolean(query.orderEventAutomationSource)}
            onChange={(event) =>
              setQuery((current) => ({
                ...current,
                page: 1,
                orderEventAutomationSource: event.target.checked
                  ? 'YMS_PORTARIA'
                  : undefined,
              }))
            }
          />
          <label htmlFor="saved-warehouses-only">
            Exibir apenas os armazéns salvos
          </label>
        </S.SavedOnlyFilter>
        <S.List>
          <S.ListHeader>
            <SortableHeadersGroup
              onSort={(sort) => handleSort(query, sort, setQuery)}
              currentSort={query}
            >
              <SortableHeader column="id" label="ID" />
              <SortableHeader column="tradeName" label="ARMAZÉM" />
            </SortableHeadersGroup>
            <S.AutomationHeader>AUTOMÁTICO — YMS PORTARIA</S.AutomationHeader>
          </S.ListHeader>
          {warehouses.map((warehouse) => (
            <S.ListItem key={warehouse.id}>
              <S.Column>{warehouse.id}</S.Column>
              <S.Column title={warehouse.tradeName}>
                {warehouse.tradeName} -{' '}
                {Formatter.document(warehouse.document, warehouse.documentType)}
              </S.Column>
              <S.AutomationColumn>
                <S.Checkbox
                  type="checkbox"
                  aria-label={`Automatizar apontamentos de ${warehouse.tradeName} pela Jornada do Veículo (YMS Portaria)`}
                  checked={automaticWarehouseIds.includes(warehouse.id)}
                  disabled={
                    isLoadingConfiguration ||
                    isSaving ||
                    Boolean(configurationError)
                  }
                  onChange={() => toggleWarehouse(warehouse.id)}
                />
              </S.AutomationColumn>
            </S.ListItem>
          ))}
        </S.List>
        <Paginator
          onPageChange={(page) => setQuery((current) => ({ ...current, page }))}
          pagination={pagination}
        />
      </S.MainPanel>
    </Scaffold>
  );
};
